import React, { useCallback, useEffect, useRef, useState } from "react";
import { QuizQueue } from "../Quiz/quizQueue.ts";
import {
    advance,
    confirmLane,
    HEARTS,
    LANE_COUNT,
    moveLane,
    Pace,
    pickLane,
    prettyFront,
    RunnerMiss,
    RunnerRow,
    RunnerState,
    rowProgress,
    rowScale,
    startRun,
} from "./runnerGame.ts";
import { cardFaces } from "../cardFaces.ts";
import { getRandomInt } from "../../Shared/Helpers.tsx";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";
import { StringKey } from "../../Shared/i18n/strings.ts";

export interface RunnerResult {
    pace: Pace;
    score: number;
    wrongGates: number;
    noPicks: number;
    misses: RunnerMiss[];
    correctFronts: string[];
}

interface RunnerGameRunProps {
    queue: QuizQueue;
    pace: Pace;
    onFinish(result: RunnerResult): void;
}

/*
 * The track is drawn in percentages of a square. The horizon is HORIZON percent
 * down; a row at the runner is centred GATE_NEAR percent down, and a row at
 * scale `s` (runnerGame.ts `rowScale`) is that fraction of the way from the
 * horizon to there. Lanes fan out from the horizon the same way, so each lane's
 * centre line is straight, which is what the road drawn in the SVG below does.
 */
const HORIZON = 12;
const GATE_NEAR = 72;
const LANE_STEP = 100 / LANE_COUNT;

/** Where, in the lane's own column, a gate sits at scale `s`. Lanes converge towards the middle. */
function gateLeft(lane: number, scale: number): number {
    const centre = 50 + (lane - (LANE_COUNT - 1) / 2) * LANE_STEP * scale;
    return ((centre - lane * LANE_STEP) / LANE_STEP) * 100;
}

function gateTop(scale: number): number {
    return HORIZON + (GATE_NEAR - HORIZON) * scale;
}

/** Fewest pixels a finger has to travel sideways to be a swipe, and how far off-axis it may drift. */
const SWIPE_MIN_DISTANCE = 40;
const SWIPE_MAX_OFF_AXIS = 0.75;

/** What the last row was, in words, for the verdict line. */
function rowText(t: (key: StringKey) => string, row: RunnerRow): string {
    const front = prettyFront(row.front);
    if (row.kind === "passed") {
        return t("flashCards.runnerPassed");
    }
    return format(
        row.kind === "late" ? t("flashCards.runnerNoPickTemplate") : t("flashCards.runnerStumbledTemplate"),
        { front, answer: row.answer }
    );
}

const VERDICT_CLASS: Record<RunnerRow["kind"], string> = {
    passed: "correct",
    wrong: "wrong",
    late: "skipped",
};

/** The runner, seen from behind: an original little figure with a cap and a backpack. */
function Runner() {
    return (
        <svg className="runnerFigureSvg" viewBox="0 0 40 60" aria-hidden="true" focusable="false">
            <ellipse className="runnerShadow" cx="20" cy="57" rx="12" ry="3" />
            <g className="runnerLegLeft">
                <rect x="13" y="36" width="6" height="18" rx="3" fill="#1e3a8a" />
                <ellipse cx="16" cy="54" rx="5" ry="2.5" fill="#111827" />
            </g>
            <g className="runnerLegRight">
                <rect x="21" y="36" width="6" height="18" rx="3" fill="#1e3a8a" />
                <ellipse cx="24" cy="54" rx="5" ry="2.5" fill="#111827" />
            </g>
            <g className="runnerArmLeft">
                <rect x="6" y="20" width="6" height="15" rx="3" fill="#0f766e" />
            </g>
            <g className="runnerArmRight">
                <rect x="28" y="20" width="6" height="15" rx="3" fill="#0f766e" />
            </g>
            <rect x="11" y="18" width="18" height="21" rx="6" fill="#14b8a6" />
            <rect x="13" y="21" width="14" height="15" rx="4" fill="#f59e0b" />
            <rect x="17" y="21" width="6" height="4" rx="2" fill="#b45309" />
            <circle cx="20" cy="10" r="7.5" fill="#fcd9b6" />
            <path d="M12 10 A8 8 0 0 1 28 10 Z" fill="#dc2626" />
            <rect x="11" y="9" width="18" height="2.5" rx="1.2" fill="#b91c1c" />
        </svg>
    );
}

/** The road and its lane lines, converging on the horizon. Decoration. */
function Road() {
    // A lane step is LANE_STEP wide at the runner (scale 1, GATE_NEAR down), so at
    // the bottom edge of the box the track has widened by this much more.
    const bottomScale = (100 - HORIZON) / (GATE_NEAR - HORIZON);
    const half = (fraction: number) => 50 * fraction * bottomScale;
    return (
        <svg className="runnerRoad" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" focusable="false">
            <polygon className="runnerRoadSurface" points={`50,${HORIZON} ${50 - half(1)},100 ${50 + half(1)},100`} />
            <line className="runnerLaneLine" x1="50" y1={HORIZON} x2={50 - half(1 / 3)} y2="100" />
            <line className="runnerLaneLine" x1="50" y1={HORIZON} x2={50 + half(1 / 3)} y2="100" />
        </svg>
    );
}

/**
 * One run: three hearts, a row of gates for every fact (ADR 0124).
 *
 * The engine is pure (`runnerGame.ts`); this feeds it animation-frame deltas
 * and lane picks. State lives in a ref with a snapshot published per frame, as
 * in FootballGameRun, because `advance` draws from the queue (0053 §8).
 *
 * A pick is ← → or A D (one lane), 1 to 3 (that lane), Space or ↑ (the lane
 * the runner is in), a tap on a lane, a swipe, or one of the three buttons
 * under the track. Each lane is a button over the whole height of the track,
 * named for its number and answer, so the page reads out the same as it
 * draws; the buttons under the track are a second way in for fingers, hidden
 * from assistive technology so nothing is read twice. A live region reads each
 * new row's fact and answers, and the verdict line then reads the result.
 *
 * Play time pauses on the Pause button and by itself when the tab is hidden.
 * The fact and the numbers are hidden while paused, so a pause is not a way to
 * read them at leisure. Resuming is always a press.
 */
export function RunnerGameRun({ queue, pace, onFinish }: RunnerGameRunProps) {
    const t = useTranslation();
    const stateRef = useRef<RunnerState | null>(null);
    if (stateRef.current === null) {
        stateRef.current = startRun(pace, queue, getRandomInt);
    }
    const [view, setView] = useState<RunnerState>(stateRef.current);
    const [paused, setPaused] = useState(false);
    const pausedRef = useRef(false);
    pausedRef.current = paused;
    const reported = useRef(false);

    const over = view.phase === "over";

    useEffect(() => {
        if (paused || over) {
            return;
        }
        let frame = 0;
        let last = performance.now();
        const tick = (now: number) => {
            // advance() clamps a negative or oversized delta itself.
            const next = advance(stateRef.current!, now - last, queue, getRandomInt);
            last = now;
            // A frame that changed nothing (the countdown is over, say) re-renders nothing.
            if (next !== stateRef.current) {
                stateRef.current = next;
                setView(next);
            }
            if (next.phase !== "over") {
                frame = requestAnimationFrame(tick);
            }
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [paused, over, queue]);

    useEffect(() => {
        const onVisibility = () => {
            if (document.hidden) {
                setPaused(true);
            }
        };
        document.addEventListener("visibilitychange", onVisibility);
        return () => document.removeEventListener("visibilitychange", onVisibility);
    }, []);

    useEffect(() => {
        if (over && !reported.current) {
            reported.current = true;
            const s = stateRef.current!;
            onFinish({
                pace: s.pace,
                score: s.score,
                wrongGates: s.wrongGates,
                noPicks: s.noPicks,
                misses: s.misses,
                correctFronts: s.correctFronts,
            });
        }
    }, [over, onFinish]);

    const apply = useCallback((change: (state: RunnerState) => RunnerState) => {
        if (pausedRef.current) {
            return;
        }
        const next = change(stateRef.current!);
        if (next !== stateRef.current) {
            stateRef.current = next;
            setView(next);
        }
    }, []);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.ctrlKey || e.metaKey || e.altKey) {
                return;
            }
            const target = e.target as HTMLElement | null;
            if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) {
                return;
            }
            const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
            if (key === "ArrowLeft" || key === "a") {
                apply((s) => moveLane(s, -1));
            } else if (key === "ArrowRight" || key === "d") {
                apply((s) => moveLane(s, 1));
            } else if (key === "ArrowUp") {
                apply(confirmLane);
            } else if (key === " ") {
                // On a button, Space is that button's own press: Pause, or a lane.
                if (target && /^(BUTTON|A)$/.test(target.tagName)) {
                    return;
                }
                apply(confirmLane);
            } else if (key.length === 1 && "123".includes(key)) {
                apply((s) => pickLane(s, Number(key) - 1));
            } else {
                return;
            }
            e.preventDefault();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [apply]);

    /**
     * Swipe left and right to change lane. The lane is a button, so the browser
     * follows a swipe with a click; without the `swiped` flag a swipe would
     * also tap the lane it ended on (as in FlashCardDeck).
     */
    const touchStart = useRef<{ x: number; y: number } | null>(null);
    const swiped = useRef(false);

    function handleTouchStart(event: React.TouchEvent) {
        const touch = event.changedTouches[0];
        touchStart.current = { x: touch.clientX, y: touch.clientY };
        swiped.current = false;
    }

    function handleTouchEnd(event: React.TouchEvent) {
        const start = touchStart.current;
        touchStart.current = null;
        if (!start) {
            return;
        }
        const touch = event.changedTouches[0];
        const dx = touch.clientX - start.x;
        const dy = touch.clientY - start.y;
        if (Math.abs(dx) < SWIPE_MIN_DISTANCE || Math.abs(dy) > Math.abs(dx) * SWIPE_MAX_OFF_AXIS) {
            return;
        }
        swiped.current = true;
        apply((s) => moveLane(s, dx < 0 ? -1 : 1));
    }

    function handleTouchCancel() {
        touchStart.current = null;
        swiped.current = false;
    }

    function tapLane(lane: number) {
        if (swiped.current) {
            swiped.current = false;
            return;
        }
        apply((s) => pickLane(s, lane));
    }

    const moving = view.phase === "running" && !paused;
    const masked = paused || view.phase === "countdown";
    const settled = view.phase === "gap" || view.phase === "over";
    const row = view.lastRow;
    const faces = cardFaces(view.problem);
    const countdownSeconds = Math.max(1, Math.ceil(view.countdownLeftMs / 1000));
    const scale = rowScale(rowProgress(view));
    const verdictClass = settled && row ? VERDICT_CLASS[row.kind] : null;
    const stumbled = settled && row !== null && row.kind !== "passed";
    const announcement = moving
        ? format(t("flashCards.runnerAnnounceTemplate"), {
              front: prettyFront(faces.front),
              a: view.numbers[0],
              b: view.numbers[1],
              c: view.numbers[2],
          })
        : "";

    return (
        <div className="quizRun runnerRun">
            <div className="quizStatus runnerStatus">
                <span className="quizScore">{format(t("flashCards.runnerScoreTemplate"), { n: view.score })}</span>
                <span
                    className="runnerHearts"
                    role="img"
                    aria-label={format(t("flashCards.runnerHeartsAriaTemplate"), { n: view.hearts })}
                >
                    {Array.from({ length: HEARTS }, (_, i) => (
                        <span key={i} className={i < view.hearts ? "runnerHeart" : "runnerHeart runnerHeart--lost"}>
                            {i < view.hearts ? "♥" : "♡"}
                        </span>
                    ))}
                </span>
            </div>

            <p className="runnerFact">
                {masked ? " " : `${prettyFront(faces.front)} = ${settled && row ? row.answer : "?"}`}
            </p>

            <div className="runnerPlay">
                <div
                    className={`runnerTrack${moving ? " runnerTrack--moving" : ""}`}
                    role="group"
                    aria-label={t("flashCards.runnerTrackAria")}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                    onTouchCancel={handleTouchCancel}
                >
                    <div className="runnerSky" aria-hidden="true" />
                    <div className="runnerGrass" aria-hidden="true" />
                    <Road />

                    {Array.from({ length: LANE_COUNT }, (_, lane) => {
                        const value = view.numbers[lane];
                        const state =
                            settled && row
                                ? lane === row.rightLane
                                    ? " runnerGate--right"
                                    : lane === row.pickedLane
                                      ? " runnerGate--wrong"
                                      : ""
                                : view.picked && lane === view.lane
                                  ? " runnerGate--picked"
                                  : "";
                        return (
                            <button
                                key={lane}
                                type="button"
                                className="runnerLane"
                                style={{ left: `${lane * LANE_STEP}%` }}
                                // Not `disabled`: a disabled button drops keyboard focus.
                                aria-disabled={!moving}
                                aria-label={
                                    masked
                                        ? format(t("flashCards.runnerLaneHiddenAriaTemplate"), { n: lane + 1 })
                                        : format(t("flashCards.runnerLaneAriaTemplate"), { n: lane + 1, value })
                                }
                                onClick={() => tapLane(lane)}
                            >
                                <span
                                    className={`runnerGate${state}`}
                                    aria-hidden="true"
                                    style={{
                                        left: `${gateLeft(lane, scale)}%`,
                                        top: `${gateTop(scale)}%`,
                                        transform: `translate(-50%, -50%) scale(${scale.toFixed(3)})`,
                                    }}
                                >
                                    {masked ? "?" : value}
                                </span>
                            </button>
                        );
                    })}

                    <div
                        className={`runnerFigure${stumbled ? " runnerFigure--stumble" : ""}`}
                        style={{ left: `${(view.lane + 0.5) * LANE_STEP}%` }}
                        aria-hidden="true"
                    >
                        <Runner />
                    </div>

                    {paused ? (
                        <div className="runnerOverlay">
                            <p className="runnerOverlayTitle">{t("flashCards.gamePaused")}</p>
                            <p className="runnerOverlayHint">{t("flashCards.gamePausedHint")}</p>
                        </div>
                    ) : view.phase === "countdown" ? (
                        <div className="runnerOverlay" aria-live="assertive">
                            <p className="quizCountdownNumber">{countdownSeconds}</p>
                            <p className="runnerOverlayHint">{t("flashCards.getReady")}</p>
                        </div>
                    ) : null}
                </div>

                {/* A second way in for fingers; the lanes above are what assistive technology reads. */}
                <div className="runnerPads" aria-hidden="true">
                    {Array.from({ length: LANE_COUNT }, (_, lane) => (
                        <button
                            key={lane}
                            type="button"
                            tabIndex={-1}
                            className={`runnerPad${view.picked && lane === view.lane ? " runnerPad--picked" : ""}`}
                            onClick={() => apply((s) => pickLane(s, lane))}
                        >
                            <span className="runnerPadKey">{lane + 1}</span>
                            <span className="runnerPadValue">{masked ? "?" : view.numbers[lane]}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* The new row, read out as it appears; the verdict line reads how it went. */}
            <p className="srOnly runnerAnnounce" aria-live="polite">
                {announcement}
            </p>

            {/* Always rendered, always the same height, as on the quiz. */}
            <p className={`quizVerdict${verdictClass ? ` quizVerdict--${verdictClass}` : ""}`} aria-live="polite">
                {settled && row && <span key={row.number}>{rowText(t, row)}</span>}
            </p>

            <div className="runnerButtons">
                <button
                    type="button"
                    className="quizFinish runnerPause"
                    onClick={() => setPaused((p) => !p)}
                    disabled={over}
                >
                    {paused ? t("flashCards.gameResume") : t("flashCards.gamePause")}
                </button>
            </div>

            <p className="quizHint">{t("flashCards.runnerHint")}</p>
        </div>
    );
}
